/**
 * Microsoft Sign-In Card Component
 * Reusable component for Microsoft 365 OAuth sign-in
 */

import { useState, useRef } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Mail, LogIn, Loader2, RefreshCw } from "lucide-react";
import axios from "axios";

import { API } from "../config/api";

const MAX_AUTO_RETRIES = 2;
const RETRY_DELAY_MS = 3000;

export function MicrosoftSignInCard({ 
  returnUrl = "/", 
  title = "Connect your Microsoft account",
  description = "Sign in with your Microsoft 365 account to access your emails and file them to SharePoint.",
  className = ""
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const [warmingUp, setWarmingUp] = useState(false);
  const autoRetryRef = useRef(0);

  const attemptLogin = async (attempt = 0) => {
    try {
      const response = await axios.get(`${API}/bonsai/auth/login`, {
        params: { return_url: returnUrl },
        timeout: 30000,
      });

      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
        // Don't setLoading(false) — page is navigating away
      } else {
        setError("Microsoft sign-in is not configured. Please contact IT.");
        setLoading(false);
        setWarmingUp(false);
      }
    } catch (err) {
      const isTimeout = err.code === "ECONNABORTED" || !err.response;

      if (isTimeout && attempt < MAX_AUTO_RETRIES) {
        // Server cold-starting — auto-retry silently
        autoRetryRef.current = attempt + 1;
        setWarmingUp(true);
        setRetryCount(attempt + 1);
        setTimeout(() => attemptLogin(attempt + 1), RETRY_DELAY_MS);
        return;
      }

      console.error("Failed to initiate MS login:", err);
      setWarmingUp(false);

      if (isTimeout) {
        setError(
          "Server is taking too long to respond. This can happen after a period of inactivity — please try again in a moment."
        );
      } else if (
        err.response?.status === 400 &&
        err.response?.data?.detail?.includes("not configured")
      ) {
        setError("Microsoft sign-in is not configured. Please contact IT.");
      } else {
        setError("Failed to connect to Microsoft. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = (isManualRetry = false) => {
    setLoading(true);
    setError("");
    setWarmingUp(false);
    autoRetryRef.current = 0;
    if (isManualRetry) setRetryCount((c) => c + 1);
    attemptLogin(0);
  };

  const statusLabel = warmingUp
    ? `Server warming up — retrying (${autoRetryRef.current}/${MAX_AUTO_RETRIES})…`
    : retryCount > 0
    ? `Connecting (attempt ${retryCount + 1})…`
    : "Connecting…";

  return (
    <Card className={`border-slate-200 ${className}`} data-testid="microsoft-signin-card">
      <CardContent className="p-8 text-center">
        {/* 27 Oct 2026 system-wide refresh: gradient tile and button. */}
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 ring-1 ring-white/20 shadow-lg shadow-indigo-900/50"
          style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6 55%, #ec4899)" }}>
          <Mail className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-semibold text-slate-800 mb-2">{title}</h2>
        <p className="text-slate-500 mb-6">{description}</p>

        {error && (
          <div
            className="mb-4 p-3 rounded-md bg-status-red-dim text-status-red text-sm border border-red-100"
            data-testid="signin-error-message"
          >
            <p className="mb-2">{error}</p>
            <button
              onClick={() => handleMicrosoftLogin(true)}
              className="inline-flex items-center gap-1.5 text-xs text-status-red hover:text-status-red underline"
              data-testid="ms-retry-btn"
            >
              <RefreshCw className="w-3 h-3" />
              Try again
            </button>
          </div>
        )}

        <Button
          onClick={() => handleMicrosoftLogin(false)}
          disabled={loading}
          className="w-full h-12 text-base text-white shadow-lg shadow-indigo-950/50 transition hover:brightness-110 disabled:opacity-60"
          style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)" }}
          data-testid="microsoft-signin-button"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              {statusLabel}
            </>
          ) : (
            <>
              <LogIn className="w-5 h-5 mr-2" />
              Sign in with Microsoft
            </>
          )}
        </Button>

        {loading && (
          <p className="mt-3 text-xs text-slate-400">
            {warmingUp
              ? "The server appears to be waking up after inactivity. Retrying automatically…"
              : "Redirecting to Microsoft sign-in…"}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
